package com.eladfinish.bible.contest.screens.browse.contentOfChapter.ui

import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.PagerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.move_to_next_book
import com.eladfinish.bible.contest.move_to_previous_book
import com.eladfinish.bible.contest.screens.browse.contentOfChapter.BrowseChaptersAction
import com.eladfinish.bible.contest.screens.browse.contentOfChapter.BrowseChaptersState.PartUiState
import com.eladfinish.bible.contest.screens.browse.contentOfChapter.model.ContentMode
import com.eladfinish.bible.contest.screens.chapterComplete.features.shuffle.ui.RevealCardDataListUi
import com.eladfinish.bible.contest.ui.components.toolbar.autohide.ReportActiveScroll
import dev.icerock.moko.resources.compose.stringResource
import kotlinx.collections.immutable.ImmutableMap
import kotlinx.coroutines.flow.distinctUntilChanged

@Composable
internal fun ColumnScope.ChapterPager(
    pagerState: PagerState,
    hasBookEdges: Boolean,
    previousBookName: String,
    nextBookName: String,
    chapterState: ImmutableMap<Int, PartUiState>,
    textSize: Float,
    goToMarkerController: GoToMarkerController,
    onBookmarkScrollYChanged: (y: Int, textOffset: Int) -> Unit,
    onAction: (BrowseChaptersAction) -> Unit,
    onChapterVisible: (Int) -> Unit,
    deceasedNamePage: Int? = null,
) {
    HorizontalPager(
        state = pagerState,
        modifier = Modifier
            .fillMaxWidth()
            .weight(1f),
    ) { pageIndex ->
        when {
            hasBookEdges && pageIndex == 0 -> BookEdgePlaceholder(
                title = stringResource(SharedRes.strings.move_to_previous_book),
                bookName = previousBookName,
                align = Alignment.CenterEnd,
            )

            hasBookEdges && pageIndex == pagerState.pageCount - 1 -> BookEdgePlaceholder(
                title = stringResource(SharedRes.strings.move_to_next_book),
                bookName = nextBookName,
                align = Alignment.CenterStart,
            )

            else -> {
                ChapterPageHost(
                    chapterIndex = pageIndex,
                    partUiState = chapterState[pageIndex],
                    textSize = textSize,
                    onVisible = onChapterVisible,
                    onAction = onAction,
                    goToMarkerController = goToMarkerController,
                    onBookmarkScrollYChanged = onBookmarkScrollYChanged,
                    isActivePage = pageIndex == pagerState.currentPage,
                    hasDeceasedNameButton = pageIndex == deceasedNamePage,
                )
            }
        }
    }
}

@Composable
internal fun ObservePagerSettledNavigation(
    pagerState: PagerState,
    hasBookEdges: Boolean,
    onNavigateToPreviousBook: () -> Unit,
    onNavigateToNextBook: () -> Unit,
    onChapterSettled: (Int) -> Unit,
) {
    LaunchedEffect(pagerState, hasBookEdges) {
        snapshotFlow { pagerState.isScrollInProgress to pagerState.currentPage }
            .distinctUntilChanged()
            .collect { (isScrolling, page) ->
                if (isScrolling) return@collect

                when {
                    hasBookEdges && page == 0 -> onNavigateToPreviousBook()
                    hasBookEdges && page == pagerState.pageCount - 1 -> onNavigateToNextBook()
                    else -> onChapterSettled(page) // 1-based chapter page with book edges, 0-based without
                }
            }
    }
}

@Composable
internal fun rememberSelectedTabIndex(pagerState: PagerState) =
    remember(pagerState) { derivedStateOf { pagerState.currentPage } }

@Composable
private fun ChapterPageHost(
    chapterIndex: Int,
    partUiState: PartUiState?,
    textSize: Float,
    onVisible: (Int) -> Unit,
    onAction: (BrowseChaptersAction) -> Unit,
    goToMarkerController: GoToMarkerController,
    onBookmarkScrollYChanged: (y: Int, textOffset: Int) -> Unit,
    isActivePage: Boolean,
    hasDeceasedNameButton: Boolean,
) {
    RequestChapterOnFirstVisible(
        partIndex = chapterIndex,
        partUiState = partUiState,
        onVisible = onVisible,
    )

    val partContent: @Composable () -> Unit = {
        PartContent(
            partUiState = partUiState,
            textSize = textSize,
            onAction = onAction,
            goToMarkerController = goToMarkerController,
            onBookmarkScrollYChanged = onBookmarkScrollYChanged,
            isActivePage = isActivePage,
        )
    }

    if (hasDeceasedNameButton) {
        WithDeceasedNameButton(
            onEnterNameClick = { onAction(BrowseChaptersAction.EnterDeceasedNameClicked) },
            content = partContent,
        )
    } else {
        partContent()
    }
}

@Composable
private fun PartContent(
    partUiState: PartUiState?,
    textSize: Float,
    onAction: (BrowseChaptersAction) -> Unit,
    goToMarkerController: GoToMarkerController,
    onBookmarkScrollYChanged: (y: Int, textOffset: Int) -> Unit,
    isActivePage: Boolean,
) {
    when (partUiState) {
        PartUiState.Idle,
        PartUiState.Loading,
        null,
            -> {
            ChapterLoadingView()
        }

        is PartUiState.Error -> {
            ChapterErrorView(
                message = partUiState.message,
            )
        }

        // Only the button above the page, until a name is entered.
        PartUiState.AwaitingDeceasedName -> Unit

        is PartUiState.Ready -> {
            val mode = partUiState.contentMode

            if (mode is ContentMode.ShuffledVerses) {
                val listState = rememberLazyListState()
                ReportActiveScroll(isActivePage, listState)
                RevealCardDataListUi(
                    verses = mode.verses,
                    listState = listState,
                )
                return
            }

            ChapterTextView(
                chapterData = partUiState.chapterData,
                goToMarkerController = goToMarkerController,
                onBookmarkScrollYChanged = onBookmarkScrollYChanged,
                onAction = onAction,
                textSize = textSize,
                isActivePage = isActivePage,
                stamFont = partUiState.stamFont,
                contentMode = partUiState.contentMode,
                scrollEvent = partUiState.scrollEvent,
            )
        }
    }
}

@Composable
private fun RequestChapterOnFirstVisible(
    partIndex: Int,
    partUiState: PartUiState?,
    onVisible: (Int) -> Unit,
) {
    LaunchedEffect(partIndex, partUiState) {
        val shouldRequest =
            partUiState == null
                    || partUiState is PartUiState.Idle
                    || partUiState is PartUiState.Error

        if (shouldRequest) onVisible(partIndex)
    }
}
